import React, { useState, useMemo } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import {
  AuditTable,
  AuditFilters,
  getDateRangeValues,
  type DateRangeOption,
} from '../../AuditHistory';
import { useAuditLogs } from '../../../hooks/useAuditLogs';

interface StoreAuditTabProps {
  storeNumber: string;
}

/**
 * Activity/Audit tab for store detail view.
 * Displays audit history for the store with date range filtering.
 */
export const StoreAuditTab: React.FC<StoreAuditTabProps> = ({ storeNumber }) => {
  const [dateRange, setDateRange] = useState<DateRangeOption>('last30days');

  const { startDate, endDate } = useMemo(() => getDateRangeValues(dateRange), [dateRange]);

  const {
    logs: auditLogs,
    isLoading: auditLoading,
    error: auditError,
    loadMore: loadMoreAudit,
    hasMore: hasMoreAudit,
  } = useAuditLogs({
    storeNumber,
    startDate,
    endDate,
  });

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <AuditFilters dateRange={dateRange} onDateRangeChange={setDateRange} />
      </Box>

      <AuditTable
        logs={auditLogs}
        isLoading={auditLoading}
        showStoreColumn={false}
        showUserColumn={true}
        onLoadMore={loadMoreAudit}
        hasMore={hasMoreAudit}
      />

      {auditError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {auditError}
        </Alert>
      )}

      {!auditLoading && auditLogs.length === 0 && !auditError && (
        <Box
          sx={{
            py: 6,
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Activity Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No changes have been recorded for this store in the selected time period.
          </Typography>
        </Box>
      )}
    </>
  );
};
